import { ApolloError } from '@apollo/client'
import React, { DetailedHTMLProps } from 'react'
import ReactSwitch, { ReactSwitchProps } from 'react-switch'

type Variant = 'primary' | 'danger' | 'white'
const getVariant = (variant: Variant) => {
  switch (variant) {
    case 'primary':
      return 'blue'
    case 'danger':
      return 'red'
    default:
      return 'white'
  }
}

export const Button: React.FC<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > & { variant?: Variant }
> = ({ className = '', variant = 'primary', ...props }) => {
  return (
    <button
      className={
        props.disabled
          ? `flex justify-center items-center bg-${getVariant(
              variant
            )}-500 text-white text-2xl font-bold rounded cursor-not-allowed whitespace-no-wrap ${className}`
          : `flex justify-center items-center bg-${getVariant(
              variant
            )}-800 hover:bg-${getVariant(
              variant
            )}-900 text-white text-2xl font-bold rounded whitespace-no-wrap ${className}`
      }
      style={{ height: '44px' }}
      {...props}
    />
  )
}

export const LinkButton: React.FC<
  DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  > & { variant?: Variant }
> = ({ className = '', variant = 'primary', ...props }) => {
  return (
    <a
      className={`flex justify-center items-center bg-${getVariant(
        variant
      )}-800 hover:bg-${getVariant(
        variant
      )}-900 text-white text-2xl font-bold rounded whitespace-no-wrap ${className}`}
      style={{ height: '44px' }}
      {...props}
    />
  )
}

export const PageTitle: React.FC<DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLHeadingElement>,
  HTMLHeadingElement
>> = (props) => {
  return <h1 className={`text-blue-800 text-3xl font-bold`} {...props} />
}

export const FormControl: React.FC = ({ children }) => {
  return <div className="mb-8">{children}</div>
}

export const Label: React.FC<React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
>> = ({ children, ...props }) => {
  return (
    <label className="block text-lg font-bold mb-2" {...props}>
      {children}
    </label>
  )
}

export const Input: React.FC<React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>> = ({ className = '', ...props }) => {
  return (
    <input
      className={`w-full appearance-none rounded border border-gray-50 py-2 px-3 leading-tight focus:outline-none focus:shadow-outline ${className}`}
      {...props}
    />
  )
}

export const Textarea: React.FC<React.DetailedHTMLProps<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>> = ({ className = '', ...props }) => {
  return (
    <textarea
      className={`w-full appearance-none rounded border border-gray-50 py-2 px-3 leading-tight focus:outline-none focus:shadow-outline ${className}`}
      {...props}
    />
  )
}

export const Select: React.FC<React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
>> = ({ className = '', ...props }) => {
  return (
    <select
      className={`w-full appearance-none border border-gray-50 py-2 px-3 leading-tight focus:outline-none focus:shadow-outline ${className}`}
      {...props}
    />
  )
}

export const CheckBox: React.FC<React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>> = ({ className = '', ...props }) => {
  return (
    <input
      type="checkbox"
      className={`form-checkbox ${className}`}
      {...props}
    />
  )
}

export const ErrorMessage: React.FC<{
  error?: ApolloError
  columnName: string
}> = ({ error, columnName }) => {
  if (!error || !error.graphQLErrors || error.graphQLErrors.length === 0) {
    return <></>
  }
  const recordInvalidErrors = error.graphQLErrors.filter(
    (it) => it.extensions?.code === 'RECORD_INVALID'
  )
  /** HACK: Railsのエラーオブジェクトそのまま使っているが、ちょっと構造複雑でが苦しい感じある */
  const errors = recordInvalidErrors
    .map(
      (it) =>
        (it.extensions?.record?.errors as Array<{
          [columnName: string]: string[]
        }>) || []
    )
    .flat()
  return (
    <>
      {errors
        .filter((it) => it[columnName])
        .map((it, i) => (
          <p key={i} className="text-red-500 text-xs italic">
            {(it[columnName] || []).map((e: string, j: number) => (
              <React.Fragment key={j}>{e}</React.Fragment>
            ))}
          </p>
        ))}
    </>
  )
}

export const Switch: React.FC<ReactSwitchProps> = (props) => {
  return (
    <ReactSwitch
      onColor={'#2699FB'}
      height={38}
      width={80}
      uncheckedIcon={false}
      checkedIcon={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: 'white',
          }}
        >
          ON
        </div>
      }
      {...props}
    />
  )
}

export const Card: React.FC<React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElement>,
  HTMLElement
>> = ({ children, className = '' }) => {
  return (
    <div
      className={`bg-white rounded py-5 px-4 ${className}`}
      style={{ boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)' }}
    >
      {children}
    </div>
  )
}

export const LinkCard: React.FC<React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>> = ({ children, className = '', href = '' }) => {
  return (
    <a
      className={`bg-white rounded py-5 px-4 ${className}`}
      href={href}
      style={{ boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)' }}
    >
      {children}
    </a>
  )
}
